import { CommonModule } from '@angular/common';
import { Component, HostListener, OnInit, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ArrowLeft, Check, ChevronDown, FileText, LucideAngularModule, Plus, Save, Search, Trash2 } from 'lucide-angular';
import { catchError, finalize, of } from 'rxjs';
import { ApiService } from '../../../core/services/api.service';
import { ToastService } from '../../../shared/ui/toast.service';

interface GeneratedInvoiceApi {
  row_key: string;
  bill_id: string;
  mode?: string | null;
  invoice_type?: string | null;
  bill_for?: string | null;
  invoice_date?: string | null;
  due_date?: string | null;
  rebate_date?: string | null;
  rebate_percentage?: number | null;
  row_json?: Record<string, unknown> | null;
}

interface InvoiceOption {
  id: string;
  label: string;
  clientName: string;
  matchKeys: string[];
}

interface NoteLineRow {
  id: number;
  particulars: string;
  quantity: number;
  rate: number;
  amount: number;
}

interface NoteFormState {
  noteType: 'Credit Note' | 'Debit Note';
  noteDate: string;
  invoiceId: string;
  clientName: string;
  remarks: string;
}

interface CreditDebitNoteApi {
  id: string;
  note_type?: string;
  note_date?: string;
  invoice_id?: string;
  client_name?: string;
  remarks?: string | null;
  rows?: Array<{
    particulars?: string;
    quantity?: number | string;
    rate?: number | string;
    amount?: number | string;
  }>;
}

interface NotePrefillQuery {
  noteType: 'Credit Note' | 'Debit Note';
  noteDate: string;
  invoiceId: string;
  clientName: string;
  remarks: string;
  particulars: string;
  quantity: number;
  rate: number;
  amount: number;
}

@Component({
  standalone: true,
  imports: [CommonModule, FormsModule, LucideAngularModule],
  templateUrl: './credit-debit-note.page.html',
  styleUrl: './credit-debit-note.page.scss',
})
export class CreditDebitNotePage implements OnInit {
  protected readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly api = inject(ApiService);
  private readonly toast = inject(ToastService);

  protected readonly ArrowLeftIcon = ArrowLeft;
  protected readonly FileTextIcon = FileText;
  protected readonly SaveIcon = Save;
  protected readonly PlusIcon = Plus;
  protected readonly Trash2Icon = Trash2;
  protected readonly SearchIcon = Search;
  protected readonly ChevronDownIcon = ChevronDown;
  protected readonly CheckIcon = Check;

  protected readonly noteTypeOptions: Array<'Credit Note' | 'Debit Note'> = ['Credit Note', 'Debit Note'];
  protected readonly invoices = signal<InvoiceOption[]>([]);
  protected readonly isSaving = signal(false);
  protected readonly noteId = signal<string | null>(null);
  protected readonly clientDropdownOpen = signal(false);
  protected readonly clientSearch = signal('');
  protected readonly isEditMode = computed(() => !!this.noteId());

  protected readonly form = signal<NoteFormState>({
    noteType: 'Credit Note',
    noteDate: new Date().toISOString().slice(0, 10),
    invoiceId: '',
    clientName: '',
    remarks: '',
  });

  protected readonly lineRows = signal<NoteLineRow[]>([
    {
      id: 1,
      particulars: '',
      quantity: 1,
      rate: 0,
      amount: 0,
    },
  ]);

  protected readonly totalAmount = computed(() =>
    this.lineRows().reduce((sum, row) => sum + (Number(row.amount) || 0), 0),
  );
  protected readonly clientOptions = computed(() =>
    Array.from(
      new Set(
        this.invoices()
          .map((item) => item.clientName.trim())
          .filter((item) => !!item && item !== '-'),
      ),
    ).sort((a, b) => a.localeCompare(b)),
  );
  protected readonly filteredClientOptions = computed(() => {
    const term = this.clientSearch().trim().toLowerCase();
    if (!term) return this.clientOptions();
    return this.clientOptions().filter((item) => item.toLowerCase().includes(term));
  });
  protected readonly filteredInvoices = computed(() => {
    const selectedClient = this.form().clientName.trim();
    if (!selectedClient) return [];
    return this.findInvoicesForSelection(selectedClient);
  });

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id');
    if (id) this.noteId.set(id);

    this.api
      .get<GeneratedInvoiceApi[]>('/api/invoices/generated')
      .pipe(catchError(() => of([])))
      .subscribe((items) => {
        const mapped = (items || [])
          .filter((item) => !!item?.bill_id)
          .map((item) => this.mapInvoice(item));
        this.invoices.set(mapped);
        if (id) {
          this.loadNoteForEdit(id);
          return;
        }
        this.applyPrefillFromQuery();
      });
  }

  @HostListener('document:click')
  protected closeClientDropdownOnOutsideClick(): void {
    this.clientDropdownOpen.set(false);
  }

  protected patchForm(patch: Partial<NoteFormState>): void {
    this.form.update((current) => ({ ...current, ...patch }));
  }

  protected toggleClientDropdown(event: Event): void {
    event.stopPropagation();
    this.clientDropdownOpen.update((open) => !open);
  }

  protected onClientPanelClick(event: Event): void {
    event.stopPropagation();
  }

  protected onClientSearchChange(value: string): void {
    this.clientSearch.set(value);
  }

  protected selectClient(clientName: string): void {
    this.patchForm({ clientName, invoiceId: '' });
    this.clientDropdownOpen.set(false);
    this.clientSearch.set('');
  }

  protected selectedClientLabel(): string {
    const selected = this.form().clientName.trim();
    return selected || 'Select Client';
  }

  protected isClientSelected(clientName: string): boolean {
    return this.form().clientName.trim().toLowerCase() === clientName.trim().toLowerCase();
  }

  protected onInvoiceChange(invoiceId: string): void {
    this.patchForm({ invoiceId });
  }

  protected addRow(): void {
    const nextId = this.lineRows().length ? Math.max(...this.lineRows().map((item) => item.id)) + 1 : 1;
    this.lineRows.update((rows) => [
      ...rows,
      {
        id: nextId,
        particulars: '',
        quantity: 1,
        rate: 0,
        amount: 0,
      },
    ]);
  }

  protected removeRow(id: number): void {
    if (this.lineRows().length === 1) {
      this.toast.show('At least one row is required', '', 'danger');
      return;
    }
    this.lineRows.update((rows) => rows.filter((row) => row.id !== id));
  }

  protected updateRow(id: number, patch: Partial<NoteLineRow>): void {
    this.lineRows.update((rows) =>
      rows.map((row) => {
        if (row.id !== id) return row;
        const next = { ...row, ...patch };
        const qty = Number(next.quantity) || 0;
        const rate = Number(next.rate) || 0;
        next.amount = Number((qty * rate).toFixed(2));
        return next;
      }),
    );
  }

  protected submit(): void {
    const value = this.form();
    if (!value.clientName.trim()) {
      this.toast.show('Validation error', 'Please select client.', 'danger');
      return;
    }

    const validRows = this.lineRows().filter((row) => row.particulars.trim() && row.amount > 0);
    if (validRows.length === 0) {
      this.toast.show('Validation error', 'Add at least one valid row with amount.', 'danger');
      return;
    }

    const payload = {
      note_type: value.noteType,
      note_date: value.noteDate,
      invoice_id: value.invoiceId,
      client_name: value.clientName,
      remarks: value.remarks,
      total_amount: this.totalAmount(),
      rows: this.lineRows().map((row) => ({
        particulars: row.particulars,
        quantity: Number(row.quantity) || 0,
        rate: Number(row.rate) || 0,
        amount: Number(row.amount) || 0,
      })),
    };

    this.isSaving.set(true);
    const request$ = this.isEditMode()
      ? this.api.patch<{ id?: string }>(`/api/credit-debit-notes/${this.noteId()}`, payload)
      : this.api.post<{ id?: string }>('/api/credit-debit-notes', payload);
    request$
      .pipe(
        catchError(() => {
          this.toast.show('Saved locally', 'Backend API not available, but form is validated.', 'info');
          return of({});
        }),
        finalize(() => this.isSaving.set(false)),
      )
      .subscribe(() => {
        this.toast.show(this.isEditMode() ? 'Note updated successfully' : 'Note saved successfully', '', 'success');
        if (this.isEditMode()) {
          this.router.navigateByUrl('/credit-debit-notes');
          return;
        }
        this.resetForm();
      });
  }

  private resetForm(): void {
    this.form.set({
      noteType: 'Credit Note',
      noteDate: new Date().toISOString().slice(0, 10),
      invoiceId: '',
      clientName: '',
      remarks: '',
    });
    this.lineRows.set([
      {
        id: 1,
        particulars: '',
        quantity: 1,
        rate: 0,
        amount: 0,
      },
    ]);
    this.clientSearch.set('');
    this.clientDropdownOpen.set(false);
  }

  private applyPrefillFromQuery(): void {
    const prefills = this.readPrefillQuery();
    const resolvedClientName = this.resolveClientName(prefills.clientName);
    const resolvedInvoiceId = this.resolveInvoiceId(prefills.clientName || resolvedClientName, prefills.invoiceId);

    this.form.update((current) => ({
      ...current,
      noteType: prefills.noteType,
      noteDate: prefills.noteDate,
      invoiceId: resolvedInvoiceId,
      clientName: resolvedClientName,
      remarks: prefills.remarks,
    }));

    if (prefills.particulars || prefills.amount > 0) {
      this.lineRows.set([
        {
          id: 1,
          particulars: prefills.particulars,
          quantity: prefills.quantity,
          rate: prefills.rate,
          amount: prefills.amount,
        },
      ]);
    }
  }

  private resolveClientName(value: string): string {
    const clientName = value.trim();
    if (!clientName) return '';

    const exact = this.clientOptions().find((item) => item.toLowerCase() === clientName.toLowerCase());
    if (exact) return exact;

    const normalizedValue = this.normalizeClientName(clientName);
    const normalizedExact = this.clientOptions().find((item) => this.normalizeClientName(item) === normalizedValue);
    if (normalizedExact) return normalizedExact;

    const partial = this.clientOptions().find((item) => {
      const normalizedItem = this.normalizeClientName(item);
      return normalizedItem.includes(normalizedValue) || normalizedValue.includes(normalizedItem);
    });
    if (partial) return partial;

    const matchedInvoices = this.findInvoicesForSelection(clientName);
    const matchedClientNames = Array.from(
      new Set(matchedInvoices.map((item) => item.clientName.trim()).filter((item) => !!item && item !== '-')),
    );
    if (matchedClientNames.length === 1) {
      return matchedClientNames[0];
    }

    return clientName;
  }

  private resolveInvoiceId(clientName: string, invoiceId: string): string {
    const requestedInvoiceId = invoiceId.trim();
    const availableInvoices = this.findInvoicesForSelection(clientName);

    if (!availableInvoices.length) return requestedInvoiceId;
    if (requestedInvoiceId && availableInvoices.some((item) => item.id === requestedInvoiceId)) {
      return requestedInvoiceId;
    }
    if (availableInvoices.length === 1) {
      return availableInvoices[0].id;
    }
    return requestedInvoiceId;
  }

  private normalizeClientName(value: string): string {
    return value
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  private findInvoicesForSelection(value: string): InvoiceOption[] {
    const normalizedValue = this.normalizeClientName(value);
    if (!normalizedValue) return [];
    return this.invoices().filter((item) => item.matchKeys.includes(normalizedValue));
  }

  private readPrefillQuery(): NotePrefillQuery {
    const params = this.route.snapshot.queryParamMap;
    const noteType = params.get('noteType') === 'Debit Note' ? 'Debit Note' : 'Credit Note';
    const noteDate = this.normalizeDate(params.get('noteDate')) || new Date().toISOString().slice(0, 10);
    const quantity = this.parsePositiveNumber(params.get('quantity'), 1);
    const rate = this.parsePositiveNumber(params.get('rate'));
    const amount = this.parsePositiveNumber(params.get('amount'), rate * quantity);

    return {
      noteType,
      noteDate,
      invoiceId: (params.get('invoiceId') || '').trim(),
      clientName: (params.get('clientName') || '').trim(),
      remarks: (params.get('remarks') || '').trim(),
      particulars: (params.get('particulars') || '').trim(),
      quantity,
      rate,
      amount,
    };
  }

  private normalizeDate(value: string | null): string {
    if (!value) return '';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return '';
    return parsed.toISOString().slice(0, 10);
  }

  private parsePositiveNumber(value: string | null, fallback = 0): number {
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : fallback;
  }

  private mapInvoice(item: GeneratedInvoiceApi): InvoiceOption {
    const row = item.row_json || {};
    const clientName =
      this.readText(row, ['buyer', 'buyer_name', 'clientName', 'client_name', 'vendor_name']) ||
      this.readText(row, ['seller', 'seller_name']) ||
      '-';
    const matchKeys = Array.from(
      new Set(
        [
          clientName,
          this.readText(row, ['buyer', 'buyer_name']),
          this.readText(row, ['seller', 'seller_name']),
          this.readText(row, ['contract_number', 'contract_id']),
          this.readText(row, ['application_no', 'application_id']),
          item.bill_id,
        ]
          .map((value) => this.normalizeClientName(value || ''))
          .filter((value) => !!value),
      ),
    );

    return {
      id: item.bill_id,
      label: item.bill_id,
      clientName,
      matchKeys,
    };
  }

  private readText(row: Record<string, unknown>, keys: string[]): string {
    for (const key of keys) {
      const value = row[key];
      if (typeof value === 'string' && value.trim()) return value.trim();
    }
    return '';
  }

  private loadNoteForEdit(id: string): void {
    this.api
      .get<CreditDebitNoteApi>(`/api/credit-debit-notes/${id}`)
      .pipe(catchError(() => of(null)))
      .subscribe((note) => {
        if (!note) return;
        const noteType = (note.note_type || '').toLowerCase().includes('debit') ? 'Debit Note' : 'Credit Note';
        this.form.set({
          noteType,
          noteDate: (note.note_date || new Date().toISOString().slice(0, 10)).slice(0, 10),
          invoiceId: note.invoice_id || '',
          clientName: note.client_name || '',
          remarks: note.remarks || '',
        });

        const rows = (note.rows || []).map((row, index) => {
          const quantity = Number(row.quantity) || 0;
          const rate = Number(row.rate) || 0;
          const amount = Number(row.amount) || Number((quantity * rate).toFixed(2));
          return {
            id: index + 1,
            particulars: row.particulars || '',
            quantity,
            rate,
            amount,
          };
        });
        this.lineRows.set(
          rows.length
            ? rows
            : [
                {
                  id: 1,
                  particulars: '',
                  quantity: 1,
                  rate: 0,
                  amount: 0,
                },
              ],
        );
      });
  }
}
